/**
 * Markup tool registration system
 * Feature: 049-critique-photo-markup
 *
 * Extensible plugin system for markup tools - each tool registers metadata
 * including handle positions, icons, labels, and resize behavior
 */

import type { MarkupShape, ShapeType } from '@/types/markup.types';

/**
 * Handle position in percentage coordinates (0-100)
 */
export interface HandlePosition {
  id: 'handle1' | 'handle2';
  x: number; // X coordinate (0-100 percentage)
  y: number; // Y coordinate (0-100 percentage, scaled by aspect ratio)
  cursor: string; // CSS cursor style
  label: string; // Accessible label for the handle
}

/**
 * Props required by all drawing tool components
 */
export interface DrawingToolProps {
  currentStyle: {
    strokeWidth: number;
    strokeColor: string;
    strokeDasharray: string;
  };
  onShapeComplete: (shape: MarkupShape) => void;
  onValidationError: (message: string) => void;
  onToolClick: (x: number, y: number) => number | null;
  onShapeClick: (shapeNumber: number) => void; // Direct shape selection by number
  selectedShapeId: number | null; // Currently selected shape number (for handle interaction)
  shapes: MarkupShape[];
  onShapeMove: (shapeNumber: number, shape: MarkupShape) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  photoAspectRatio?: number;
}

/**
 * Render props passed to shape renderers
 */
export interface ShapeRenderProps {
  shape: MarkupShape;
  isSelected: boolean;
  onMouseDown: (e: React.MouseEvent<SVGElement>) => void;
  viewBoxHeight: number;
}

/**
 * Tool metadata provided by each tool during registration
 */
export interface ToolMetadata {
  type: ShapeType;
  label: string; // Display name (e.g., "Circle", "Rectangle", "Arrow")
  icon?: React.ComponentType<{ className?: string }>; // SVG icon component
  zOrder?: number; // Rendering z-order (lower = behind, higher = in front, default: 0)

  /**
   * React component for drawing this shape type
   */
  Component: React.ComponentType<DrawingToolProps>;

  /**
   * Calculate handle positions for a shape of this type
   * Returns array of handles for resizing/adjusting the shape
   */
  getHandles: (shape: MarkupShape) => HandlePosition[];

  /**
   * Update shape geometry when a handle is dragged
   * @param shape - Original shape before handle drag
   * @param handleId - Which handle is being dragged
   * @param x - New X position in percentage (0-100)
   * @param y - New Y position in percentage (0-100, scaled by aspect ratio)
   * @param viewBoxHeight - Height of viewBox for aspect ratio adjustment
   * @returns Updated shape with new geometry
   */
  updateFromHandle: (
    shape: MarkupShape,
    handleId: 'handle1' | 'handle2',
    x: number,
    y: number,
    viewBoxHeight: number
  ) => MarkupShape;

  /**
   * Translate (move) the entire shape by deltaX and deltaY
   * @param shape - Original shape before translation
   * @param deltaX - X offset in percentage (0-100)
   * @param deltaY - Y offset in percentage (0-viewBoxHeight)
   * @param viewBoxHeight - Height of viewBox for aspect ratio adjustment
   * @returns Updated shape with translated geometry
   */
  translateShape: (
    shape: MarkupShape,
    deltaX: number,
    deltaY: number,
    viewBoxHeight: number
  ) => MarkupShape;

  /**
   * Render the shape as SVG elements
   * @returns React element(s) to render the shape
   */
  renderShape: (props: ShapeRenderProps) => React.ReactElement;
}

/**
 * Global tool registry
 */
const toolRegistry = new Map<ShapeType, ToolMetadata>();

/**
 * Register a tool with its metadata
 */
export function registerTool(metadata: ToolMetadata): void {
  toolRegistry.set(metadata.type, metadata);
}

/**
 * Get metadata for a specific tool type
 */
export function getToolMetadata(type: ShapeType): ToolMetadata | undefined {
  return toolRegistry.get(type);
}

/**
 * Get all registered tools
 */
export function getAllTools(): ToolMetadata[] {
  return Array.from(toolRegistry.values());
}

/**
 * Get z-order for a shape (uses registered tool metadata, defaults to 0)
 */
export function getShapeZOrder(shape: MarkupShape): number {
  const metadata = toolRegistry.get(shape.type);
  return metadata?.zOrder ?? 0;
}

/**
 * Get handle positions for a shape (uses registered tool metadata)
 */
export function getShapeHandles(shape: MarkupShape): HandlePosition[] {
  const metadata = toolRegistry.get(shape.type);
  return metadata ? metadata.getHandles(shape) : [];
}

/**
 * Update shape from handle drag (uses registered tool metadata)
 */
export function updateShapeFromHandle(
  shape: MarkupShape,
  handleId: 'handle1' | 'handle2',
  x: number,
  y: number,
  viewBoxHeight: number
): MarkupShape {
  const metadata = toolRegistry.get(shape.type);
  if (!metadata) {
    return shape; // No metadata, return unchanged
  }
  return metadata.updateFromHandle(shape, handleId, x, y, viewBoxHeight);
}

/**
 * Translate shape by deltaX and deltaY (uses registered tool metadata)
 */
export function translateShape(
  shape: MarkupShape,
  deltaX: number,
  deltaY: number,
  viewBoxHeight: number
): MarkupShape {
  const metadata = toolRegistry.get(shape.type);
  if (!metadata) {
    return shape; // No metadata, return unchanged
  }
  return metadata.translateShape(shape, deltaX, deltaY, viewBoxHeight);
}

/**
 * Render shape as SVG (uses registered tool metadata)
 */
export function renderShape(props: ShapeRenderProps): React.ReactElement | null {
  const metadata = toolRegistry.get(props.shape.type);
  if (!metadata) {
    return null; // No metadata, render nothing
  }
  return metadata.renderShape(props);
}
